export const DARK_PALETTE = [
  "#2F5B8A", // corporate blue
  "#6B9E7A", // sage green
  "#B07D5B", // warm tan
  "#8B6BAE", // muted purple
  "#5B9EA6", // teal
  "#A68B5B", // khaki
  "#C65B5B", // muted red
  "#4B7B9E", // steel blue
  "#7A8A9E", // slate
  "#8FA06B", // olive
  "#B06B8B", // dusty rose
  "#A09060", // bronze
] as const;

const BRAND_COLORS: Record<string, string> = {
  Arc: "#E75D8B",
  Ghostty: "#5068D8",
  Salesforce: "#00A1E0",
  Excel: "#217346",
  "Microsoft Excel": "#217346",
  Outlook: "#0078D4",
  "Microsoft Outlook": "#0078D4",
  "Microsoft Teams": "#6264A7",
  Notion: "#37352F",
  Figma: "#A259FF",
  ChatGPT: "#10A37F",
  YouTube: "#FF0000",
  LinkedIn: "#0A66C2",
  Slack: "#4A154B",
  WhatsApp: "#25D366",
  Gmail: "#EA4335",
  "Google Chrome": "#4285F4",
  Claude: "#D4A574",
  Obsidian: "#7C3AED",
  "Twitter/X": "#1DA1F2",
  X: "#1DA1F2",
  GitHub: "#AAAAAA",
  Anki: "#2196F3",
  Terminal: "#666666",
  "Google Sheets": "#0F9D58",
  "Google Meet": "#00897B",
  Zoom: "#2D8CFF",
  Discord: "#5865F2",
  Telegram: "#0088CC",
  Teams: "#6264A7",
  "VS Code": "#007ACC",
  Cursor: "#007ACC",
  Finder: "#1C9AE0",
  Safari: "#0078FF",
  WordReference: "#D35400",
  Preview: "#888888",
};

const hashStr = (s: string): number => {
  let acc = 0;
  for (let i = 0; i < s.length; i++) {
    acc = Math.imul(acc, 31) + s.charCodeAt(i);
    acc = acc >>> 0; // keep unsigned
  }
  return acc;
};

export const getNodeColor = (appName: string): string =>
  BRAND_COLORS[appName] ?? DARK_PALETTE[hashStr(appName) % DARK_PALETTE.length];

export const getTaskColor = (index: number): string => DARK_PALETTE[index % DARK_PALETTE.length];

// Identity, rather than list position or display name, keeps colours stable across views.
/** Up to two initials for a person's avatar, ignoring parenthesised notes like "(You)". */
export const personInitials = (label: string, locale?: string): string =>
  label
    .replace(/\([^)]*\)/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word[0])
    .join("")
    .toLocaleUpperCase(locale);

export const getPersonColor = (personId: string, opacity = 1): string =>
  `hsla(${hashStr(personId) % 360}, 45%, 38%, ${opacity})`;

// Separate hues for adjacent Overview rows; reserve gray for the "Other" group.
const ALLOCATION_PALETTE = [
  "#587CA4", // blue
  "#B58B4A", // ochre
  "#5B927D", // green
  "#9275AA", // lavender
  "#B66F7C", // rose
  "#599BA6", // teal
  "#B47B59", // terracotta
  "#899650", // olive
  "#A66C98", // mauve
  "#6E73A8", // indigo
];

export const getAllocationColor = (index: number): string =>
  ALLOCATION_PALETTE[index] ??
  `hsl(${((index - ALLOCATION_PALETTE.length) * 137.508 + 20) % 360} 30% 48%)`;

/** Color used for insignificant ("irrelevant") processes in charts and the timeline. */
export const INSIGNIFICANT_COLOR = "#9AA3AF";

// Independent app rows can share a brand accent; unknown apps stay neutral.
const normalizedBrandColors = new Map(
  Object.entries(BRAND_COLORS).map(([name, color]) => [
    name.replace(/\s+/g, "").toLowerCase(),
    color,
  ]),
);
export const appPrimaryColor = (name: string): string =>
  normalizedBrandColors.get(name.replace(/\s+/g, "").toLowerCase()) ?? "#7B8798";
